import React, { useState } from 'react';
import { Typography, styled } from '@mui/material';
import Column from 'src/frontend-common/components/Column';
import { useResendActivationMutation } from 'src/redux/services/auth';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import FidoLogo from 'src/frontend-common/components/Logo';
import ConfirmationEmailCircle from 'src/frontend-common/components/Icons/ConfirmationEmailCircle';
import CustomCircularProgress from 'src/components/CustomCircularProgress/CustomCircularProgress';
import ButtonComponent from 'src/components/ButtonComponent';

const Container = styled('div')(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5),
  width: '100%',
  alignSelf: 'center',
  [theme.breakpoints.up('xl')]: {
    maxWidth: '1002.5px',
    padding: theme.spacing(15),
  },
}));

const Spacer = styled(Column)(({ theme }) => ({
  marginBottom: theme.spacing(5),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 32,
  lineHeight: 1.2,
  color: theme.palette.primary.main,
  marginBottom: 20,
  textAlign: 'center',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  lineHeight: 1.1,
  color: theme.palette.text.primary,
  textAlign: 'center',
}));

const Error = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  color: 'red',
}));

const CustomButton = styled(ButtonComponent)(({ theme }) => ({
  height: 56,
  width: '100%',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(5),
  },
}));

export const RegistrationCompletePage = () => {
  const [resendActivationMutation, { isError, isLoading, error }] = useResendActivationMutation();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resendEnable, setResendEnable] = useState<boolean>(false);

  const username = (location.state as { username: string } | undefined)?.username ?? '';

  const resendCallback = useDebouncedCallback(() => {
    resendActivationMutation({ username });
    setResendEnable(false);
  }, 200);

  React.useEffect(() => {
    if (isLoading) {
      const errorData = error as {
        data: { message: string; violations: Record<string, string> };
      };
      setErrorMessage(errorData.data.violations['__all'] ?? 'Generic error');
    }
  }, [isError]);

  return (
    <Container>
      <div style={{ marginBottom: 95 }}>
        <FidoLogo />
      </div>
      <div style={{ marginBottom: 24 }}>
        <ConfirmationEmailCircle />
      </div>

      <Spacer centered>
        <Title style={{ fontSize: 40, fontWeight: 600, color: 'black' }}>Confirm your email</Title>
        <>{errorMessage && <Error>{errorMessage}</Error>}</>
        <Subtitle>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 400,
              color: '#616161',
              marginBottom: 24,
            }}
          >
            We have successfully created your new account. But before you start, you will have to activate it. We have sent an{' '}
            <b style={{ color: 'black' }}>activation mail</b> to the email you provided during registration. It should arrive in a couple of minutes.
          </Typography>
          <Typography
            style={{
              marginBottom: 24,
              fontSize: 14,
              fontWeight: 400,
              color: '#616161',
            }}
          >
            If the email has not arrived during 30s you can still click the button below to resend it. We guarantee it will come this time!
          </Typography>
        </Subtitle>
      </Spacer>
      {!resendEnable && (
        <div style={{ marginBottom: '30px' }}>
          <CustomCircularProgress start={30} onTimerEnds={() => setResendEnable(true)} />
        </div>
      )}
      <Column centered>
        <CustomButton
          buttontype="mainaction"
          style={{ backgroundColor: isLoading || !resendEnable ? '' : 'black' }}
          disabled={isLoading || !resendEnable}
          disableElevation={true}
          onClick={resendCallback.callback}
          text="Re-send the verification email"
        />
      </Column>
    </Container>
  );
};
